import type { WorkType } from '../common/models';

export type WorkLogEntry = {
    date: string;
    hours: number;
    note: string | null;
};

// A logged entry for a member's month, with its work-item context — the
// calendar groups these by date to render each day's cell.
export type MonthWorkLog = {
    date: string;
    workItemId: number;
    title: string;
    workItemType: string;
    workType: WorkType | null;
    hours: number;
    note: string | null;
};

export type WorkLogEntryWriteResult = {
    workItemId: number;
    completedWork: number;
    writtenEntries: WorkLogEntry[];
};

export type ApplyWorkLogEntryCommand =
    | { action: 'Add'; workItemId: number; date: string; hours: number; note?: string | null }
    | { action: 'Remove'; workItemId: number; date: string; hours: number };

export type ApplyWorkLogEntriesCommand = {
    entries: ApplyWorkLogEntryCommand[];
};

export type ApplyWorkLogEntriesResult = {
    items: WorkLogEntryWriteResult[];
};

export type PendingAction = 'add' | 'update' | 'remove';

export type PendingEntry = {
    id: string;
    workItemId: number;
    workItemTitle: string;
    workItemType: string;
    date: string;
    hours: number;
    action: PendingAction;
};

export type PendingGroup = readonly [date: string, entries: PendingEntry[]];

const sortByDateThenItem = (a: PendingEntry, b: PendingEntry): number => {
    if (a.date !== b.date) {
        return a.date.localeCompare(b.date);
    }
    return a.workItemId - b.workItemId;
};

export const groupPendingByDate = (pending: readonly PendingEntry[]): PendingGroup[] => {
    const sorted = pending.toSorted(sortByDateThenItem);
    const map = new Map<string, PendingEntry[]>();
    for (const entry of sorted) {
        const list = map.get(entry.date) ?? [];
        list.push(entry);
        map.set(entry.date, list);
    }
    return [...map.entries()];
};
